/**
 * `chartGeometry` — Task 157, pure SVG-chart math (no React, no DOM).
 *
 * Powers `PerformanceTimeSeriesChart`: given a list of numeric values and a
 * viewport box, it produces the screen-space point coordinates, the polyline
 * + area `d` path strings, a "nice" y-axis scale, and tick labels.
 *
 * Coordinate convention: time flows LEFT (oldest, index 0) → RIGHT (newest,
 * last index) inside the SVG's own coordinate space. The chart's internal
 * axis is intentionally kept LTR even though the surrounding app is RTL —
 * this is the standard, least-surprising reading order for a time series.
 * The Arabic labels / tooltips live in the React layer; this module only
 * does numbers.
 *
 * Safety contract (exhaustively tested): every exported function returns
 * finite numbers / well-formed strings for empty, single-point, and
 * all-equal (incl. all-zero) inputs. No `NaN`, no `Infinity`, no `-0` leaks
 * into a path or a scale bound.
 */

export interface ChartDimensions {
  /** Total SVG viewport width (user units). */
  readonly width: number;
  /** Total SVG viewport height (user units). */
  readonly height: number;
  /** Inner padding (user units) reserved on every side for axes/labels. */
  readonly padding: number;
}

export interface ChartScale {
  /** Lower bound of the (nice) value axis. */
  readonly min: number;
  /** Upper bound of the (nice) value axis. */
  readonly max: number;
}

export interface ChartPoint {
  /** Original index in the input series (0 = oldest, leftmost). */
  readonly index: number;
  /** Original data value at this index. */
  readonly value: number;
  /** Screen-space x (increases left→right). */
  readonly x: number;
  /** Screen-space y (increases top→bottom; high values sit near the top). */
  readonly y: number;
}

const FALLBACK_DIMENSION = 1;

function safe(n: number, fallback = 0): number {
  return Number.isFinite(n) ? n : fallback;
}

/** Inner plot rectangle after subtracting padding, clamped to be non-negative. */
function innerBox(dim: ChartDimensions): {
  readonly left: number;
  readonly top: number;
  readonly innerWidth: number;
  readonly innerHeight: number;
} {
  const width = Math.max(safe(dim.width, FALLBACK_DIMENSION), 0);
  const height = Math.max(safe(dim.height, FALLBACK_DIMENSION), 0);
  const padding = Math.max(safe(dim.padding, 0), 0);
  // Never let padding eat the whole box (would yield negative inner span).
  const usablePadX = Math.min(padding, width / 2);
  const usablePadY = Math.min(padding, height / 2);
  return {
    left: usablePadX,
    top: usablePadY,
    innerWidth: Math.max(width - usablePadX * 2, 0),
    innerHeight: Math.max(height - usablePadY * 2, 0),
  };
}

/**
 * Round a positive span up to a "nice" step (1·10ⁿ, 2·10ⁿ, 5·10ⁿ).
 * `niceStep(0)` and non-finite input collapse to `1` so callers never divide
 * by zero.
 */
export function niceStep(rawStep: number): number {
  const step = safe(rawStep, 0);
  if (step <= 0) return 1;
  const exponent = Math.floor(Math.log10(step));
  const magnitude = Math.pow(10, exponent);
  const fraction = step / magnitude;
  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  const result = niceFraction * magnitude;
  return result > 0 && Number.isFinite(result) ? result : 1;
}

/**
 * Compute a rounded ("nice") value-axis domain for `values`.
 *
 * Rules:
 *  - Empty series → `{ min: 0, max: 1 }` (a unit axis, never degenerate).
 *  - All-equal series (incl. all-zero): a band is synthesized around the
 *    value so the line renders mid-box rather than collapsing to an edge.
 *  - Otherwise the min is floored toward zero when the data is non-negative
 *    (the common ad-metrics case) and both bounds are snapped to the step.
 */
export function computeScale(values: ReadonlyArray<number>, tickCount = 4): ChartScale {
  const finite = values.map((v) => safe(v, 0));
  if (finite.length === 0) {
    return { min: 0, max: 1 };
  }

  let dataMin = Math.min(...finite);
  const dataMax = Math.max(...finite);

  if (dataMin === dataMax) {
    // Degenerate (all values identical). Build a symmetric-ish band.
    const v = dataMin;
    if (v === 0) return { min: 0, max: 1 };
    if (v > 0) return { min: 0, max: niceStep(v) };
    // All-negative constant.
    return { min: -niceStep(-v), max: 0 };
  }

  // Non-negative data anchors the baseline at zero (standard for spend/impr).
  if (dataMin >= 0) dataMin = 0;

  const span = dataMax - dataMin;
  const n = Math.max(1, Math.floor(safe(tickCount, 4)));
  const step = niceStep(span / n);
  const niceMin = Math.floor(dataMin / step) * step;
  const niceMax = Math.ceil(dataMax / step) * step;
  const min = safe(niceMin, 0);
  const max = safe(niceMax, min + 1);
  return max > min ? { min, max } : { min, max: min + 1 };
}

/**
 * Evenly spaced, "nice"-stepped tick values across `[min, max]` inclusive.
 * Always returns at least two ticks (the two bounds) and never emits a
 * non-finite tick.
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const lo = safe(min, 0);
  const hi = safe(max, lo + 1);
  if (hi <= lo) return [lo, lo + 1];
  const n = Math.max(1, Math.floor(safe(count, 4)));
  const step = niceStep((hi - lo) / n);
  const ticks: number[] = [];
  // Accumulate from lo; guard the loop bound against fp drift.
  for (let t = lo; t <= hi + step * 1e-9; t += step) {
    ticks.push(safe(Number(t.toFixed(10)), lo));
    if (ticks.length > 64) break; // hard safety cap
  }
  if (ticks.length === 0) return [lo, hi];
  // Ensure the upper bound is represented.
  if (Math.abs(ticks[ticks.length - 1] - hi) > step * 1e-6) ticks.push(hi);
  return ticks;
}

/**
 * Project each value into screen space.
 *
 *  - x is distributed evenly left→right across the inner width. A single
 *    point is centered.
 *  - y maps `scale.min`→bottom and `scale.max`→top (SVG y grows downward).
 */
export function computePoints(
  values: ReadonlyArray<number | null>,
  dim: ChartDimensions,
  scale: ChartScale,
): ChartPoint[] {
  const { left, top, innerWidth, innerHeight } = innerBox(dim);
  const lo = safe(scale.min, 0);
  const hiRaw = safe(scale.max, lo + 1);
  const hi = hiRaw > lo ? hiRaw : lo + 1;
  const denom = hi - lo;

  const n = values.length;
  if (n === 0) return [];

  // `null` / non-finite entries are GAPS: they yield no point, and
  // buildLinePath / buildAreaPath break the path across them. x is keyed to
  // the ORIGINAL index so the surviving points keep their true date spacing —
  // a null ratio day (zero-denominator CTR/ROAS) must NOT compact the axis or
  // get a line drawn across it (Codex P2).
  const out: ChartPoint[] = [];
  values.forEach((raw, index) => {
    if (raw === null || raw === undefined || !Number.isFinite(raw)) return;
    const value = safe(raw, 0);
    const x = n === 1 ? left + innerWidth / 2 : left + (innerWidth * index) / (n - 1);
    // Fraction of the way from min→max, clamped to [0,1].
    const frac = Math.min(Math.max((value - lo) / denom, 0), 1);
    const y = top + innerHeight * (1 - frac);
    out.push({ index, value, x: safe(x, left), y: safe(y, top) });
  });
  return out;
}

function fmt(n: number): string {
  // Trim to a stable, NaN-free token. 3 decimals is plenty for path coords.
  const v = safe(n, 0);
  return Number(v.toFixed(3)).toString();
}

/**
 * Polyline `d` for the series line. Empty / single-point series degrade
 * gracefully (`''` for empty, a tiny `M…L…` dot-segment for a single point so
 * the marker still has an anchor). Never contains `NaN`.
 */
export function buildLinePath(points: ReadonlyArray<ChartPoint>): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const p = points[0];
    return `M${fmt(p.x)} ${fmt(p.y)}L${fmt(p.x)} ${fmt(p.y)}`;
  }
  // Break the polyline into sub-paths wherever the ORIGINAL indices are not
  // contiguous — a gap means one or more null days, and the line must NOT be
  // drawn across them (Codex P2). Each run starts a fresh `M`.
  const parts: string[] = [];
  let prevIndex: number | null = null;
  for (const p of points) {
    const cmd = prevIndex === null || p.index !== prevIndex + 1 ? 'M' : 'L';
    parts.push(`${cmd}${fmt(p.x)} ${fmt(p.y)}`);
    prevIndex = p.index;
  }
  return parts.join(' ');
}

/**
 * Closed area `d` (line, then down to the baseline and back). The baseline is
 * the y of `scale.min`. Returns `''` for an empty series.
 */
export function buildAreaPath(points: ReadonlyArray<ChartPoint>, dim: ChartDimensions): string {
  if (points.length === 0) return '';
  const { top, innerHeight } = innerBox(dim);
  const baselineY = top + innerHeight; // y of scale.min (bottom of plot)
  // One closed area per CONTIGUOUS run — split at null-day gaps so the fill
  // does not bridge missing days (Codex P2).
  const segments: string[] = [];
  let run: ChartPoint[] = [];
  const flush = (): void => {
    if (run.length === 0) return;
    const first = run[0];
    const last = run[run.length - 1];
    const line = run.map((p, i) => `${i === 0 ? 'M' : 'L'}${fmt(p.x)} ${fmt(p.y)}`).join(' ');
    segments.push(`${line} L${fmt(last.x)} ${fmt(baselineY)} L${fmt(first.x)} ${fmt(baselineY)} Z`);
    run = [];
  };
  let prevIndex: number | null = null;
  for (const p of points) {
    if (prevIndex !== null && p.index !== prevIndex + 1) flush();
    run.push(p);
    prevIndex = p.index;
  }
  flush();
  return segments.join(' ');
}

/**
 * Compact axis-value label (e.g. `1.2K`, `3.4M`, `12`). Always finite-safe.
 * Used for both y-axis ticks and tooltips of large counts.
 */
export function formatAxisValue(value: number): string {
  const v = safe(value, 0);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${trimNum(v / 1_000_000)}M`;
  if (abs >= 1_000) return `${trimNum(v / 1_000)}K`;
  if (abs < 1 && abs > 0) return trimNum(v, 2);
  return trimNum(v, abs >= 100 ? 0 : 1);
}

function trimNum(n: number, maxFractionDigits = 1): string {
  const v = safe(n, 0);
  return v.toLocaleString('en-US', { maximumFractionDigits: maxFractionDigits });
}
